import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Send, Sparkles, ArrowRight, CheckCircle2, Boxes, Pencil, Check, Loader2, Bookmark, BookmarkCheck, X } from 'lucide-react';
import type { ParticipantHomeData } from '../../../types/aai';
import { aaiApi } from '../../../services/aaiApi';
import { Card, Hero, MonoLabel, Banner, Bar, StageTrack } from './ui';
import { CertBadge } from '../components';

// Editable profile-card summary (F6, P1). Self-contained: fetches its current
// value from the caller's own readiness brief and PATCHes /profile on save.
const ProfileSummaryCard: React.FC = () => {
  const [summary, setSummary] = useState('');
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    aaiApi.getMyReadiness().then(({ data }) => {
      const v = data.brief?.fields?.['profile.summary']?.value;
      if (typeof v === 'string') setSummary(v);
    }).catch(() => undefined);
  }, []);
  const save = async () => {
    setBusy(true);
    try { const { data } = await aaiApi.editProfileSummary(draft.trim()); setSummary(data.summary); setEditing(false); }
    finally { setBusy(false); }
  };
  return (
    <Card>
      <div className="flex items-center justify-between mb-2">
        <MonoLabel>About my AI work</MonoLabel>
        {!editing && <button onClick={() => { setDraft(summary); setEditing(true); }} className="text-gray-300 hover:text-brand-500"><Pencil size={13} /></button>}
      </div>
      {editing ? (
        <div>
          <textarea autoFocus value={draft} onChange={(e) => setDraft(e.target.value)} rows={3} placeholder="A line or two about how you use AI and what you want to get better at…"
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-brand-200" />
          <div className="flex items-center gap-2 mt-2">
            <button disabled={busy} onClick={save} className="inline-flex items-center gap-1.5 text-[12px] font-semibold bg-brand-500 text-white px-3 py-1.5 rounded-lg disabled:opacity-50 whitespace-nowrap">{busy ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Save</button>
            <button onClick={() => setEditing(false)} className="text-[12px] text-gray-500">Cancel</button>
          </div>
        </div>
      ) : (
        <p className="text-[13px] text-gray-600 leading-relaxed">{summary || <span className="text-gray-400 italic">Add a short profile so your champion and trainer know how you work with AI.</span>}</p>
      )}
    </Card>
  );
};

// Starter-skill recommendations (F6, P1) — each can be dismissed or saved.
// Persisted in localStorage (keyed per skill) so a dismissed rec doesn't reappear
// on the next dashboard load and a saved one stays flagged.
const REC_DISMISSED = 'aai:rec:dismissed';
const REC_SAVED = 'aai:rec:saved';
const loadIdSet = (key: string): Set<string> => {
  try { return new Set(JSON.parse(localStorage.getItem(key) || '[]')); } catch { return new Set(); }
};
const persistIdSet = (key: string, s: Set<string>) => {
  try { localStorage.setItem(key, JSON.stringify(Array.from(s))); } catch { /* ignore quota */ }
};

const RecommendationList: React.FC<{
  recs: { id: string; name: string; reason: string }[];
  onOpen: () => void;
}> = ({ recs, onOpen }) => {
  const [dismissed, setDismissed] = useState<Set<string>>(() => loadIdSet(REC_DISMISSED));
  const [saved, setSaved] = useState<Set<string>>(() => loadIdSet(REC_SAVED));
  const dismiss = (id: string) => { const n = new Set(dismissed); n.add(id); setDismissed(n); persistIdSet(REC_DISMISSED, n); };
  const toggleSave = (id: string) => {
    const n = new Set(saved); n.has(id) ? n.delete(id) : n.add(id); setSaved(n); persistIdSet(REC_SAVED, n);
  };
  const visible = recs.filter((r) => !dismissed.has(r.id));
  if (visible.length === 0) return <p className="text-[13px] text-gray-400">No recommendations right now — you’ve cleared them all.</p>;
  return (
    <div className="space-y-2">
      {visible.map((rec) => (
        <div key={rec.id} className="flex items-center gap-2 bg-gray-50 hover:bg-brand-50 rounded-lg px-3 py-2.5 transition-colors">
          <button onClick={onOpen} className="min-w-0 flex-1 text-left">
            <div className="font-semibold text-[13px] truncate">{rec.name}</div>
            <div className="text-[11.5px] text-gray-500">{saved.has(rec.id) ? 'Saved' : rec.reason}</div>
          </button>
          <button title={saved.has(rec.id) ? 'Saved — click to unsave' : 'Save for later'} onClick={() => toggleSave(rec.id)}
            className="shrink-0 text-gray-300 hover:text-brand-500">
            {saved.has(rec.id) ? <BookmarkCheck size={15} className="text-brand-500" /> : <Bookmark size={15} />}
          </button>
          <button title="Dismiss" onClick={() => dismiss(rec.id)} className="shrink-0 text-gray-300 hover:text-red-500">
            <X size={15} />
          </button>
        </div>
      ))}
    </div>
  );
};

const STATUS_LABEL: Record<string, { text: string; cls: string }> = {
  complete: { text: 'Complete', cls: 'text-emerald-600' },
  in_progress: { text: 'In progress', cls: 'text-brand-600' },
  current: { text: 'Now', cls: 'text-brand-600' },
  locked: { text: 'Locked', cls: 'text-gray-400' },
};

// What each request status means to the person who raised it, in plain words.
const REQ_MSG: Record<string, string> = {
  pending: 'awaiting your department head',
  needs_info: 'your dept head asked for more detail',
  dept_approved: 'approved by your dept head — now awaiting the AI champion',
};

const ParticipantHome: React.FC<{ data: ParticipantHomeData }> = ({ data }) => {
  const navigate = useNavigate();
  const stage = data.readiness?.stage as string | undefined;

  return (
    <div className="space-y-4">
      <Banner tone="blue" icon={<Lock size={16} className="text-brand-500 shrink-0" />}>
        Private to you and your AI coach. Your manager sees team rollups — never your individual numbers.
      </Banner>

      {/* readiness hero + time redirected */}
      <div className="grid grid-cols-1 md:grid-cols-[1.5fr_1fr] gap-4">
        <Hero>
          <div className="flex items-center justify-between gap-2 mb-2.5">
            <MonoLabel tone="accent">Your readiness</MonoLabel>
            <CertBadge />
          </div>
          {data.readiness ? (
            <>
              <div className="text-3xl font-bold leading-tight mb-1">
                You're <span className="text-emerald-400 capitalize">{stage}</span>
                {data.trend.improved && data.trend.previous_stage && (
                  <span className="text-base font-medium text-white/60"> — up from {data.trend.previous_stage}</span>
                )}
              </div>
              <p className="text-[13px] text-white/60 mt-1 mb-5">Measured against your own pre-AI baseline. Re-checked monthly.</p>
              <StageTrack stage={stage} dark />
            </>
          ) : (
            <>
              <div className="text-2xl font-bold leading-tight mb-2">Let's map where you are with AI</div>
              <p className="text-[13px] text-white/60 mb-4">A quick ~5-minute onboarding builds your private readiness profile.</p>
              <button onClick={() => navigate('/onboarding')} className="inline-flex items-center gap-1.5 bg-white text-black text-[13px] font-semibold px-4 py-2 rounded-full whitespace-nowrap">
                Start onboarding <ArrowRight size={15} />
              </button>
            </>
          )}
        </Hero>

        <Card>
          <MonoLabel className="mb-2">Time redirected</MonoLabel>
          <div className="text-3xl font-bold tracking-tight">{data.time_redirected.low}–{data.time_redirected.high}
            <span className="text-[15px] font-medium text-gray-500"> {data.time_redirected.unit}</span>
          </div>
          <p className="text-xs text-gray-500 mt-1.5 leading-relaxed">Estimated time moved to higher-value work, net of rework. A range — not a hard number.</p>
        </Card>
      </div>

      {/* journey */}
      <Card>
        <MonoLabel className="mb-3.5">Your journey</MonoLabel>
        <div className="flex flex-wrap gap-3">
          {data.journey.map((j) => {
            const meta = STATUS_LABEL[j.status] || STATUS_LABEL.locked;
            return (
              <div key={j.key} className={`flex-1 min-w-[140px] bg-white rounded-lg p-3.5 ${j.status === 'locked' ? 'opacity-60' : ''}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold text-[13.5px]">{j.label}</span>
                  <span className={`text-[11px] font-semibold ${meta.cls}`}>{meta.text}</span>
                </div>
                <Bar pct={`${j.pct}%`} color={j.status === 'complete' || j.status === 'current' ? 'green' : 'brand'} track="bg-gray-100" height="h-1.5" />
                <p className="text-[11.5px] text-gray-400 mt-2">{j.note}</p>
              </div>
            );
          })}
        </div>
      </Card>

      {/* next step — the next scheduled session for this person's cohort */}
      {data.next_session && (
        <div className="bg-white border border-gray-100 rounded-xl p-5 flex items-center gap-4">
          <span className="w-10 h-10 shrink-0 rounded-xl bg-brand-50 text-brand-600 grid place-items-center"><CheckCircle2 size={20} /></span>
          <div className="flex-1 min-w-0">
            <div className="text-xs text-gray-500">Your next step{data.next_session.cohort_name ? ` · ${data.next_session.cohort_name}` : ''}</div>
            <div className="font-semibold text-[15px] text-gray-900">{data.next_session.title}</div>
          </div>
          {data.next_session.scheduled_at && (
            <span className="text-[12.5px] font-semibold text-white bg-brand-500 rounded-full px-3.5 py-1.5 whitespace-nowrap">
              {new Date(data.next_session.scheduled_at).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
            </span>
          )}
        </div>
      )}

      {/* skills + request */}
      <div className="grid grid-cols-1 md:grid-cols-[1.3fr_1fr] gap-4">
        <div className="bg-white border border-gray-100 rounded-xl p-5">
          <div className="flex items-center gap-2.5 mb-3">
            <span className="w-9 h-9 rounded-lg bg-brand-50 text-brand-600 grid place-items-center"><Send size={18} /></span>
            <div>
              <div className="font-bold text-sm">Have a problem worth solving?</div>
              <div className="text-xs text-gray-500">Describe it — it routes to your dept head, then a champion.</div>
            </div>
          </div>
          <p className="text-[13px] text-gray-600 mb-3 leading-relaxed">You ask for the pipeline; you don't open it yourself. Approved requests get scoped and built.</p>
          <button onClick={() => navigate('/requests')} className="inline-flex items-center gap-1.5 bg-brand-500 hover:bg-brand-600 text-white text-[12.5px] font-semibold px-4 py-2 rounded-full transition-colors whitespace-nowrap">
            Request something
          </button>
        </div>

        <div className="bg-white border border-gray-100 rounded-xl p-5">
          <div className="font-bold text-sm mb-3">Skills you've built</div>
          {data.skills.length === 0 ? (
            <p className="text-[13px] text-gray-400">No skills yet — they appear as you onboard and attend sessions.</p>
          ) : (
            data.skills.map((sk) => (
              <div key={sk.name} className="flex items-center gap-2.5 py-2 border-b border-gray-50 last:border-0">
                <span className="w-7 h-7 rounded-md bg-accent-100 text-amber-700 grid place-items-center"><Sparkles size={14} /></span>
                <span className="text-[13px] font-medium">{sk.name}</span>
              </div>
            ))
          )}
        </div>
      </div>

      {/* recommended starter skills (F6) + editable profile card — once onboarded */}
      {data.readiness && (
        <div className="grid grid-cols-1 md:grid-cols-[1.3fr_1fr] gap-4">
          <div className="bg-white border border-gray-100 rounded-xl p-5">
            <div className="flex items-center gap-2.5 mb-3">
              <span className="w-9 h-9 rounded-lg bg-brand-50 text-brand-600 grid place-items-center"><Boxes size={18} /></span>
              <div className="font-bold text-sm">Recommended for you</div>
            </div>
            {data.recommendations.length === 0 ? (
              <p className="text-[13px] text-gray-400">Starter skills appear here once your marketplace has published skills.</p>
            ) : (
              <RecommendationList recs={data.recommendations} onOpen={() => navigate('/aai/skills')} />
            )}
          </div>
          <ProfileSummaryCard />
        </div>
      )}

      {/* pending requests */}
      {data.pending_requests.map((r) => (
        <div key={r.id} className="flex items-center gap-2.5 bg-brand-50 rounded-lg px-3.5 py-2.5">
          <span className="font-sans text-[10.5px] font-semibold text-brand-600 bg-white px-2 py-0.5 rounded uppercase">{r.status.replace('_', ' ')}</span>
          <span className="text-[12.5px] text-brand-800 flex-1">Your request <b>"{r.title}"</b> — {REQ_MSG[r.status] || 'in review'}.</span>
          {r.created_at && <span className="text-[11px] text-gray-400 font-sans">{new Date(r.created_at).toLocaleDateString()}</span>}
        </div>
      ))}
    </div>
  );
};

export default ParticipantHome;
